import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import Lead from '../../@types/Lead';
import { debounce } from 'lodash';
import LastKey from '../../@types/LastKey';
import { toast } from 'react-toastify';
import Card from '../common/Card/Card';
import axios from '../../api/axios';
import { Checkbox, Divider, IconButton, List, ListItem, ListItemText, Menu, MenuItem, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import LoadingButton from '../common/LoadingButton/LoadingButton';
import moment from 'moment';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import Button from '../common/Button';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Link from '../common/Link/Link';
import { isMadJaxAdmin } from '../../utils/isMadJaxAdmin';
import DeleteIcon from '@mui/icons-material/Delete';
import { useConfirmationModalContext } from '../../context/ConfirmModalContext';
import LoadingIndicator from '../common/LoadingIndicator/LoadingIndicator';

interface LeadsListProps {
   dealerId?: string,
   pageSize?: number,
   madjaxAdmin?: boolean
}

const LeadsList: FC<LeadsListProps> = ({ dealerId, pageSize, madjaxAdmin = false }) => {
   const { user } = useAuth();
   const [leads, setLeads] = useState<Lead[]>([]);
   const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
   const [loading, setLoading] = useState<boolean>(true);
   const [loadingMore, setLoadingMore] = useState<boolean>(false);
   const [lastKey, setLastKey] = useState<LastKey | undefined>();
   const theme = useTheme();
   const isTablet = useMediaQuery(theme.breakpoints.down('tabletL'));
   const navigate = useNavigate();
   const [statusFilters, setStatusFilters] = useState<string[]>([]);
   const confirmContext = useConfirmationModalContext();

   const loadLeads = useCallback(async (statusFilters: string[] = []) => {
      try {
         setLoading(true);
         const queryParams: any = {};
         if (!madjaxAdmin) {
            queryParams.dealerId = dealerId ?? user?.companyAttributes?.dealerId
         }
         queryParams.pageSize = pageSize ?? 5;
         if (statusFilters.length) {
            queryParams.filter = statusFilters.join(',')
         }
         const response = await axios.get('/leads', { params: queryParams });
         const key: LastKey = response.data.lastEvalutedKey;
         setLastKey((lastKey) => key !== lastKey ? key : undefined);
         const leads = response.data.data as Lead[];
         setLeads(leads);
      } catch (error) {
         console.log(error);
         toast.error('Server error');
      } finally {
         setLoading(false);
      }
   }, [user, setLeads]);

   const loadMoreLeads = async () => {
      try {
         if (lastKey) {
            setLoadingMore(true);
            const queryParams: any = {};
            if (!madjaxAdmin) {
               queryParams.dealerId = dealerId ?? user?.companyAttributes?.dealerId
            }
            if (statusFilters.length) {
               queryParams.filter = statusFilters.join(',')
            }
            queryParams.pageSize = pageSize ?? 5;
            queryParams.pk = lastKey.pk;
            queryParams.sk = lastKey.sk;
            const response = await axios.get(`/leads`, { params: queryParams });
            const key: LastKey = response.data.lastEvalutedKey;
            setLastKey((lastKey) => key !== lastKey ? key : undefined);
            setLeads([...leads, ...response.data.data]);
         }
      } catch (error) {
         console.log(error);
         toast.error('Server error');
      } finally {
         setLoadingMore(false);
      }
   }

   const loadLeadsDebounced = useMemo(() => debounce(loadLeads, 250), [loadLeads]);

   useEffect(() => {
      loadLeadsDebounced(statusFilters);
      setLoading(true);
   }, [statusFilters, loadLeadsDebounced, setLoading]);

   const navigateToLead = (lead: Lead) => {
      if (lead.dealerId === user?.companyAttributes?.dealerId && !isMadJaxAdmin(user)) {
         navigate(`/leads/${lead.pk}`);
      } else {
         navigate(`./${lead.pk}`)
      }
   }

   const deleteLead = async (lead: Lead, index: number) => {

      try {

         setLoadingDelete(true);

         const result = await confirmContext.showConfirmation(`
            Delete Lead`,
            `Are you sure you want to delete this lead? Prospect - ${lead?.firstName} ${lead.lastName} | Dealer - ${lead.dealer?.metaData?.attributes?.companyName} (${lead.dealerId})`)

         if (result) {

            await axios.delete('/lead', { data: lead });

            setLeads((current) => current.filter((currentLead) => currentLead.pk !== lead.pk));

            toast.success('Lead deleted');

         }

      } catch (error) {

         console.log(error);

         toast.error('Server Error');

      } finally {

         setLoadingDelete(false);

      }

   }

   return (
      <Card>
         {!isTablet
            ? (
               <LeadsListTable
                  isLoading={loading}
                  isLoadingMore={loadingMore}
                  hasMore={lastKey !== undefined}
                  loadMore={loadMoreLeads}
                  leads={leads}
                  statusFilters={statusFilters}
                  setStatusFilters={setStatusFilters}
                  navigateToLead={navigateToLead}
                  dealerId={dealerId}
                  deleteLead={deleteLead}
                  loadingDelete={loadingDelete} />
            )
            : (
               <LeadsListMobile
                  isLoading={loading}
                  isLoadingMore={loadingMore}
                  hasMore={lastKey !== undefined}
                  loadMore={loadMoreLeads}
                  leads={leads}
                  statusFilters={statusFilters}
                  setStatusFilters={setStatusFilters}
                  navigateToLead={navigateToLead}
                  dealerId={dealerId}
                  deleteLead={deleteLead}
                  loadingDelete={loadingDelete} />
            )}
      </Card>
   );
}

interface LeadsListTableProps {
   isLoading: boolean,
   isLoadingMore: boolean,
   hasMore: boolean,
   loadMore: () => void,
   leads: Lead[],
   statusFilters: string[],
   setStatusFilters: (filters: string[]) => void,
   navigateToLead: (lead: Lead) => void,
   dealerId?: string,
   deleteLead: (lead: Lead, index: number) => void;
   loadingDelete: boolean
}

const LeadsListTable: FC<LeadsListTableProps> = ({
   isLoading,
   isLoadingMore,
   hasMore,
   loadMore,
   leads,
   statusFilters,
   setStatusFilters,
   navigateToLead,
   dealerId,
   deleteLead,
   loadingDelete
}) => {

   const displayCount = 5;

   const { user } = useAuth();

   const tableHeaders = user?.attributes?.type !== 'admin' ?
      ['prospect', 'contact info', 'lead type', 'status', 'date created', ''] :
      ['dealer', 'prospect', 'lead type', 'status', 'date created', ''];

   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const handleSetAnchor = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
   const menuIsOpen = Boolean(anchorEl);
   const handleCloseMenu = () => setAnchorEl(null);

   const handleStatusFilter = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, status: string) => {
      event.preventDefault();
      const filterActive = statusFilters.some((filter) => filter === status);
      const newStatusFilters = [...statusFilters];
      filterActive ?
         newStatusFilters.splice(statusFilters.indexOf(status), 1) :
         newStatusFilters.push(status);
      setStatusFilters(newStatusFilters);
   }

   return (

      <TableContainer>
         <Table>
            <TableHead>
               <TableRow>
                  {tableHeaders.map((header, index) => (
                     <TableCell
                        key={index}
                        sx={{
                           ...((header === 'lead type'
                              || header === 'status'
                              || header === 'date created'
                           )) && {
                              textAlign: 'center'
                           },
                           ...((header === 'prospect / assigned dealer')) && {
                              maxWidth: '100px'
                           },
                           borderLeft: 'none',
                           borderRight: 'none',
                           borderTop: 'none',
                           textTransform: 'uppercase'
                        }}>
                        <Typography sx={{
                           fontSize: '14px', textWrap: 'nowrap',
                           ...((header === 'status')) && {
                              paddingLeft: '24px'
                           },
                        }}>
                           {header}
                           {header === 'status' &&
                              <>
                                 <IconButton onClick={handleSetAnchor}>
                                    <FilterAltIcon width="12px" />
                                 </IconButton>
                                 <Menu
                                    id="FilterMenu"
                                    anchorEl={anchorEl}
                                    open={menuIsOpen}
                                    onClose={handleCloseMenu}>
                                    <MenuItem selected={false} onClick={(event) => { handleStatusFilter(event, 'Pending') }}>
                                       <Checkbox
                                          sx={{
                                             "&.Mui-checked": {
                                                color: "#3D4449"
                                             }
                                          }} checked={statusFilters?.some((filter) => filter === 'Pending')} />
                                       Pending
                                    </MenuItem>
                                    <MenuItem selected={false} onClick={(event) => { handleStatusFilter(event, 'Viewed') }}>
                                       <Checkbox
                                          sx={{
                                             "&.Mui-checked": {
                                                color: "#3D4449"
                                             }
                                          }} checked={statusFilters?.some((filter) => filter === 'Viewed')} />
                                       Viewed
                                    </MenuItem>
                                    <MenuItem sx={{ justifyContent: 'center' }} onClick={() => { setStatusFilters([]) }}>
                                       Clear All
                                    </MenuItem>
                                 </Menu>
                              </>
                           }
                        </Typography>
                     </TableCell>
                  ))}
               </TableRow>
            </TableHead>
            <TableBody>
               {!isLoading
                  ? (leads.length ? (leads.map((lead, index) => (
                     <TableRow
                        key={lead.pk}
                        sx={{
                           '.MuiTableCell-root': {
                              border: 'none',
                              paddingTop: '12px',
                              paddingBottom: '12px'
                           }
                        }}>
                        <TableCell>
                           {user?.attributes?.type !== 'admin'
                              ? (<>{lead.firstName} {lead.lastName}</>)
                              : (
                                 <Stack direction="column">
                                    <Typography>{lead?.dealer?.metaData?.attributes?.companyName}</Typography>
                                    <Typography>({lead.dealerId})</Typography>
                                 </Stack>
                              )
                           }
                        </TableCell>
                        <TableCell>
                           <Stack direction="column">
                              {user?.attributes?.type === 'admin' &&
                                 <Typography>{lead.firstName} {lead.lastName}</Typography>
                              }
                              <Typography>{lead.email}</Typography>
                              <Typography>{lead.phone}</Typography>
                           </Stack>
                        </TableCell>
                        <TableCell sx={{
                           textAlign: 'center'
                        }}>
                           {lead.leadType}
                        </TableCell>
                        <TableCell sx={{
                           textAlign: 'center'
                        }}>
                           {lead.leadStatus}
                        </TableCell>
                        <TableCell sx={{
                           textAlign: 'center'
                        }}>
                           {moment(new Date(lead.dateCreated)).format('MM/DD/YYYY')}
                        </TableCell>
                        <TableCell>
                           <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                              <Tooltip title='View Details' disableInteractive>
                                 <IconButton onClick={() => navigateToLead(lead)}>
                                    <FormatListBulletedOutlinedIcon />
                                 </IconButton>
                              </Tooltip>
                              {isMadJaxAdmin(user) &&
                                 (loadingDelete
                                    ? (<LoadingIndicator loading={loadingDelete} size={18} />)
                                    : (
                                       <Tooltip title="Delete" disableInteractive>
                                          <IconButton onClick={() => deleteLead(lead, index)}>
                                             <DeleteIcon />
                                          </IconButton>
                                       </Tooltip>
                                    )
                                 )
                              }
                           </Stack>
                        </TableCell>
                     </TableRow>
                  ))) : (
                     <TableRow
                        sx={{
                           '.MuiTableCell-root': {
                              border: 'none',
                              paddingTop: '12px',
                              paddingBottom: '12px'
                           }
                        }}>
                        <TableCell colSpan={5}>
                           <Stack
                              direction="column"
                              alignItems="center"
                              justifyContent="center"
                              spacing={2}
                              sx={{
                                 marginTop: '20px',
                                 marginBottom: '20px',
                                 textTransform: 'uppercase',
                                 textAlign: 'center'
                              }}>
                              {statusFilters.length
                                 ? (
                                    <Typography>
                                       {user?.attributes?.type !== 'admin'
                                          ? `No leads with status filter:[${statusFilters.join(',')}].  Please adjust your filters.`
                                          : `No leads with status filter:[${statusFilters.join(',')}].  Please adjust your filters.`
                                       }
                                    </Typography>
                                 )
                                 : (
                                    <Typography>
                                       {user?.attributes?.type !== 'admin'
                                          ? 'You have no leads assigned yet. Email notifications will go out when new leads are assigned.'
                                          : `No leads assigned to DEALERID#${dealerId ?? user?.companyAttributes?.dealerId}`
                                       }
                                    </Typography>)
                              }
                              {statusFilters.length < 1 &&
                                 <Typography>
                                    {user?.attributes?.type !== 'admin'
                                       ? 'Please ensure your address book is up-to-date with all your locations'
                                       : 'Please ensure the dealers address book is up-to-date with all locations'
                                    }
                                 </Typography>
                              }
                              {statusFilters.length < 1 &&
                                 <Link hrefRelative={true} href='/dealer-profile' hrefText={"View Address Book"}></Link>
                              }
                           </Stack>
                        </TableCell>
                     </TableRow>
                  ))
                  : (Array(displayCount).fill(null).map((value, index) => (
                     <TableRow
                        key={index}
                        sx={{
                           '.MuiTableCell-root': {
                              border: 'none',
                              paddingTop: '12px',
                              paddingBottom: '12px'
                           }
                        }}>
                        <TableCell>
                           <Skeleton animation="wave" width={'100%'} height={20} />
                        </TableCell>
                        <TableCell>
                           <Skeleton animation="wave" width={'100%'} height={20} />
                        </TableCell>
                        <TableCell>
                           <Skeleton animation="wave" width={'100%'} height={20} />
                        </TableCell>
                        <TableCell>
                           <Skeleton animation="wave" width={'100%'} height={20} />
                        </TableCell>
                        <TableCell>
                           <Skeleton animation="wave" width={'100%'} height={20} />
                        </TableCell>
                        <TableCell>
                           <Stack direction="row" justifyContent="flex-start" spacing={2} >
                              <Skeleton animation="wave" width={24} height={24} />
                              <Skeleton animation="wave" width={24} height={24} />
                           </Stack>
                        </TableCell>
                     </TableRow>
                  )))
               }
            </TableBody>
         </Table>
         {
            hasMore &&
            <Stack alignItems='center' sx={{ padding: '24px' }}>
               <LoadingButton loading={isLoadingMore} onClick={loadMore}>Load More</LoadingButton>
            </Stack>
         }
      </TableContainer >

   )

}

const LeadsListMobile: FC<LeadsListTableProps> = ({
   isLoading,
   isLoadingMore,
   hasMore,
   loadMore,
   leads,
   navigateToLead,
   dealerId,
   deleteLead,
   loadingDelete
}) => {

   const displayCount = 5;

   const { user } = useAuth();

   return (
      <Stack direction="column" spacing={2} divider={<Divider />}>
         {!isLoading
            ? (leads.length
               ? (leads.map((lead, index) =>
                  <List key={index}
                     sx={{
                        paddingLeft: '25px',
                        paddingRight: '25px',
                        '.MuiListItem-root': {
                           paddingLeft: 0,
                           paddingRight: 0,
                           '.MuiListItemText-root:last-of-type': {
                              textAlign: 'right',
                              maxWidth: '50%'
                           }
                        }
                     }}>
                     {user?.attributes?.type === 'admin' &&
                        <ListItem>
                           <ListItemText>Dealer</ListItemText>
                           <ListItemText>{lead?.dealer?.metaData?.attributes?.companyName} ({lead.dealerId})</ListItemText>
                        </ListItem>
                     }
                     <ListItem>
                        <ListItemText>Prospect</ListItemText>
                        <ListItemText>{lead.firstName} {lead.lastName}</ListItemText>
                     </ListItem>
                     <ListItem>
                        <ListItemText>Contact Info</ListItemText>
                        <ListItemText>
                           <Stack direction="column">
                              <Typography sx={{ wordBreak: 'break-all' }}>{lead.email}</Typography>
                              <Typography>{lead.phone}</Typography>
                           </Stack>
                        </ListItemText>
                     </ListItem>
                     <ListItem>
                        <ListItemText>Lead Type</ListItemText>
                        <ListItemText>{lead.leadType}</ListItemText>
                     </ListItem>
                     <ListItem>
                        <ListItemText>Status</ListItemText>
                        <ListItemText>{lead.leadStatus}</ListItemText>
                     </ListItem>
                     <ListItem>
                        <ListItemText>Date Created</ListItemText>
                        <ListItemText>{moment(new Date(lead.dateCreated)).format('MM/DD/YYYY')}</ListItemText>
                     </ListItem>
                     <Stack
                        direction="column"
                        spacing={2}
                        sx={{ paddingTop: '8px', paddingBottom: '8px' }}>
                        <LoadingButton
                           loading={loadingDelete}
                           onClick={() => deleteLead(lead, index)}>
                           Delete Lead
                        </LoadingButton>
                        <Button
                           onClick={() => navigateToLead(lead)}>
                           View Details
                        </Button>
                     </Stack>
                  </List>
               ))
               : (user?.attributes?.type !== 'admin'
                  ? (
                     <Stack
                        direction="column"
                        spacing={2}
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                           minHeight: '330px',
                           textAlign: 'center',
                           textTransform: 'uppercase',
                        }}>
                        <Typography>
                           You have no leads assigned yet. Email notifications will go out when new leads are assigned.
                        </Typography>
                        <Typography>
                           Please ensure your address book is up-to-date with all your locations.
                        </Typography>
                        <Link hrefRelative={true} href='../dealer-profile' hrefText={"View Address Book"}></Link>
                     </Stack>
                  )
                  : (<Stack
                     direction="column"
                     spacing={2}
                     justifyContent="center"
                     alignItems="center"
                     sx={{
                        minHeight: '330px',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                     }}>
                     <Typography>
                        No leads assigned to DEALERID#{dealerId ?? user?.companyAttributes?.dealerId}.
                     </Typography>
                  </Stack>
                  )
               )
            )
            : (Array(displayCount).fill(null).map((value, index) => (
               <List key={index}
                  sx={{
                     '.MuiListItem-root': {
                        paddingLeft: 0,
                        paddingRight: 0,
                     }
                  }}>
                  <ListItem>
                     <ListItemText>Prospect</ListItemText>
                     <Skeleton animation="wave" width={'25%'} height={20} />
                  </ListItem>
                  <ListItem>
                     <ListItemText>Contact Info</ListItemText>
                     <Skeleton animation="wave" width={'25%'} height={20} />
                  </ListItem>
                  <ListItem>
                     <ListItemText>Lead Type</ListItemText>
                     <Skeleton animation="wave" width={'50%'} height={20} />
                  </ListItem>
                  <ListItem>
                     <ListItemText>Status</ListItemText>
                     <Skeleton animation="wave" width={'50%'} height={20} />
                  </ListItem>
                  <ListItem>
                     <ListItemText>Date Created</ListItemText>
                     <Skeleton animation="wave" width={'50%'} height={20} />
                  </ListItem>
                  <ListItem>
                     <Skeleton animation="wave" width={'100%'} height={60} />
                  </ListItem>
                  <ListItem>
                     <Skeleton animation="wave" width={'100%'} height={60} />
                  </ListItem>
               </List>
            )))
         }
         {(leads.length && hasMore) &&
            <Stack alignItems='center' sx={{ padding: '24px', }}>
               <LoadingButton fullWidth={true} loading={isLoadingMore} onClick={loadMore}>Load More</LoadingButton>
            </Stack>
         }
      </Stack>
   )
}

export default LeadsList;
